<template>
  <div v-if="isFirmAdmin">
    <content-loading v-if="isLoading" />

    <empty v-else-if="!pagination || pagination.docs.length == 0">
      <h6 class="text-center">{{ $t("firm.admin-dashboard.empty") }}</h6>
    </empty>

    <div v-else class="firm-admin-column__list">
      <div v-for="item in pagination.docs" :key="item.firmId" class="firm-admin-card">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <div class="firm-admin-card__title">{{ item.name | translate | summary }}</div>
            <!-- <div class="firm-admin-card__meta text-muted">{{ item.organization.workspace.name }}</div> -->
          </div>
          <b-badge :variant="mapStage(item.status).variant" class="firm-admin-card__stage" :class="mapStage(item.status).variant">{{ mapStage(item.status).label }}</b-badge>
        </div>

        <div class="firm-admin-card__status">
          <span>{{ item.statusText }}</span>
        </div>

        <div class="firm-admin-card__metrics">
          <div class="metrics-row metrics-row--labels">
            <div class="metric metric--label">{{ $t("firm.audit.dashboard.total-questions") }}</div>
            <div class="metric metric--label">{{ $t("firm.admin-dashboard.approved") }}</div>
            <div class="metric metric--label">{{ $t("firm.admin-dashboard.needsRevision") }}</div>
            <div class="metric metric--label">{{ $t("firm.admin-dashboard.waiting") }}</div>
          </div>
          <div class="metrics-row metrics-row--values">
            <div class="metric metric--value text-primary">{{ item.questionCount }}</div>
            <div class="metric metric--value text-success">{{ item.approvedQuestionCount }}</div>
            <div class="metric metric--value text-main">{{ item.rejectedQuestionCount }}</div>
            <div class="metric metric--value text-secondary">{{ item.pendingQuestionCount }}</div>
          </div>
        </div>
      </div>
      <div class="d-flex mt-auto" v-if="pagination.totalDocs > 1">
        <b-pagination v-model="request.options.page" :total-rows="pagination.totalDocs" :per-page="pagination.limit" limit="3" size="sm"></b-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import ContentLoading from "@c/content-loading.vue";
import Empty from "@c/empty.vue";

export default {
  name: "FirmAdminColumn",
  components: { ContentLoading, Empty },
  data() {
    return {
      request: {
        options: { page: 1, limit: 2, sort: { createdAt: "desc" } },
      },
    };
  },
  computed: {
    isLoading() {
      return this.$store.getters["organizationFirm/firmWithQuestionStatLoading"];
    },
    isFirmAdmin() {
      return !!this.currentAccountHasPermissions(["FIRM_ADMIN"]);
    },

    pagination() {
      return this.$store.getters["organizationFirm/firmWithQuestionStatPagination"];
    },
  },
  watch: {
    isFirmAdmin: {
      immediate: true,
      handler(value) {
        if (value) {
          this.fetchFirms();
        }
      },
    },
    request: {
      deep: true,
      immediate: true,
      handler() {
        this.debounce(() => this.fetchFirms());
      },
    },
  },
  methods: {
    async fetchFirms() {
      try {
        await this.$store.dispatch("organizationFirm/paginateFirmWithQuestionStat", this.request);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    mapStage(status) {
      const stageMap = {
        REVIEW: { label: this.$t("firm.admin-dashboard.stage.review"), variant: "info" },
        TEST: { label: this.$t("firm.admin-dashboard.stage.test"), variant: "warning" },
        PROD: { label: this.$t("firm.admin-dashboard.stage.prod"), variant: "success" },
        CANCELED: { label: this.$t("firm.admin-dashboard.stage.canceled"), variant: "danger" },
      };
      return stageMap[status] || { label: status || "-", variant: "secondary" };
    },
  },
};
</script>

<style lang="scss" scoped>
.firm-admin-column__hint {
  font-size: 0.9rem;
  color: #6c757d;
}

.firm-admin-column__link {
  font-weight: 600;
  font-size: 0.9rem;
}

.firm-admin-column__list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.firm-admin-card {
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 0.75rem;
  background: #fdfefe;
}

.firm-admin-card__title {
  font-weight: 600;
  font-size: 1rem;
}

.firm-admin-card__meta {
  font-size: 0.85rem;
}

.firm-admin-card__status {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0.5rem 0 0.65rem;
  color: #6c757d;
  font-size: 0.9rem;
}

.firm-admin-card__metrics {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: #f5f7f9;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #eef2f7;
}

.metrics-row {
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  align-items: center;
  padding: 0.45rem 0.6rem;
}

.metrics-row + .metrics-row {
  border-top: 1px solid #e4e7ed;
}

.metric {
  flex: 1 1 0;
  min-width: 0;
  text-align: center;
  word-break: break-word;
}

.metric--label {
  font-size: 0.82rem;
  color: #6c757d;
}

.metric--value {
  font-size: 1.1rem;
  font-weight: 700;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.status-dot--info {
  background: #17a2b8;
}
.status-dot--warning {
  background: #ffc107;
}
.status-dot--success {
  background: #28a745;
}
.status-dot--danger {
  background: #dc3545;
}
.status-dot--secondary {
  background: #99a1b7;
}
</style>
